import {
	DndContext,
	DragOverlay,
	PointerSensor,
	pointerWithin,
	useDndContext,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';

import { DeleteIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Heading3, IconButton, Library, Skeleton, Tooltip } from '@treams/treams-ui';

import AddFeedbackQuestion from './AddFeedbackQuestion';
import FeedbackQuestionCard from './FeedbackQuestionCard';
import FeedbackTemplateQuestion from './FeedbackTemplateQuestion';

export type EditableTemplateQuestion = {
	question: graphql.FeedbackQuestion;
	canSubjectView: boolean;
};

function FeedbackTemplateQuestionsDraggable(props: {
	loading: string[];
	questions: EditableTemplateQuestion[];
	onAddQuestions: (questions: graphql.FeedbackQuestion[]) => void;
	onDeleteQuestion: (question: graphql.FeedbackQuestion) => Promise<void>;
	onToggleCanSubjectView: (questionID: string, canSubjectView: boolean) => void;
}) {
	const { t } = useTranslation();

	const { active } = useDndContext();

	return (
		<SortableContext
			items={props.questions.map((item) => `item:${item.question.id}`)}
			strategy={() => null}
		>
			<Heading3 mb={3}>{t('settings.feedbackLibrary.template.questions.title')}</Heading3>
			{props.questions.map((item, i) => (
				<Box key={item.question.id} mb={2}>
					<FeedbackTemplateQuestion
						index={i}
						question={item.question}
						canSubjectView={item.canSubjectView}
						loading={props.loading.includes(item.question.id)}
						onDelete={() => props.onDeleteQuestion(item.question)}
						onToggleCanSubjectView={(canSubjectView) =>
							props.onToggleCanSubjectView(item.question.id, canSubjectView)
						}
					/>
				</Box>
			))}
			<Box mb={2}>
				<AddFeedbackQuestion onSelect={props.onAddQuestions} />
			</Box>
			<DragOverlay dropAnimation={null} zIndex={10} style={{ cursor: 'grabbing' }}>
				{active && (
					<Library.Item
						embeddedHandle
						dragging
						active
						item={
							props.questions.find(({ question }) => `item:${question.id}` === active.id)!.question
						}
						buttons={[
							<Tooltip
								content={t('settings.feedbackLibrary.template.questions.remove.button')}
								portal
							>
								<IconButton type="secondary" icon={<DeleteIcon />} />
							</Tooltip>,
						]}
						renderCard={(props) => <FeedbackQuestionCard {...props} />}
					/>
				)}
			</DragOverlay>
		</SortableContext>
	);
}

export default function FeedbackTemplateQuestions(props: {
	questions: EditableTemplateQuestion[];
	onChange: (questions: EditableTemplateQuestion[]) => Promise<void>;
}) {
	const [questions, setQuestions] = useState(props.questions);
	const [loading, setLoading] = useState<Record<string, boolean>>({});

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

	return (
		<DndContext
			collisionDetection={pointerWithin}
			modifiers={[restrictToVerticalAxis]}
			sensors={sensors}
			onDragOver={(event) => {
				setQuestions((questions) => {
					if (typeof event.active.id !== 'string' || typeof event.over?.id !== 'string') {
						return questions;
					}

					const source = questions.findIndex(
						({ question }) => `item:${question.id}` === event.active.id,
					);
					const destination = questions.findIndex(
						({ question }) => `item:${question.id}` === event.over?.id,
					);

					return arrayMove(questions, source, destination);
				});
			}}
			onDragEnd={async (event) => {
				const id = event.active.id.toString().substring('item:'.length);
				setLoading((loading) => ({ ...loading, [id]: true }));
				await props.onChange(questions);
				setLoading((loading) => _.omit(loading, id));
			}}
		>
			<FeedbackTemplateQuestionsDraggable
				loading={Object.keys(loading)}
				questions={questions}
				onAddQuestions={(added) => {
					setQuestions((existing) => {
						const result = _.uniqBy(
							existing.concat(added.map((question) => ({ question, canSubjectView: true }))),
							'question.id',
						);
						(async () => {
							for (const { id } of added) {
								setLoading((loading) => ({ ...loading, [id]: true }));
							}
							await props.onChange(result);
							setLoading((loading) =>
								_.omit(
									loading,
									added.map(({ id }) => id),
								),
							);
						})();
						return result;
					});
				}}
				onDeleteQuestion={async (question) => {
					const result = questions.filter(({ question: item }) => item.id !== question.id);
					await props.onChange(result);
					setQuestions(result);
				}}
				onToggleCanSubjectView={(questionID, canSubjectView) => {
					setQuestions((existing) => {
						const result = existing.map((item) =>
							item.question.id === questionID ? { ...item, canSubjectView } : item,
						);
						(async () => {
							setLoading((loading) => ({ ...loading, [questionID]: true }));
							await props.onChange(result);
							setLoading((loading) => _.omit(loading, questionID));
						})();
						return result;
					});
				}}
			/>
		</DndContext>
	);
}

function FeedbackTemplateQuestionsSkeleton() {
	const { t } = useTranslation();

	return (
		<Box>
			<Heading3 mb={3}>{t('settings.feedbackLibrary.template.questions.title')}</Heading3>
			<Skeleton.Repeat count={3}>
				<Box mb={2}>
					<FeedbackTemplateQuestion.Skeleton />
				</Box>
			</Skeleton.Repeat>
		</Box>
	);
}

FeedbackTemplateQuestions.Skeleton = FeedbackTemplateQuestionsSkeleton;
